import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import {
  annotationCreateBatch,
  documentCreate,
  internalNoteCreate,
  participantAdd,
  participantRoundStatusCreate,
  proofingRoundCreate,
  proofingRoundDelete,
  proofingTitleUpdate,
} from "@/proofingService-client/sdk.gen";
import { getDocumentUploadSignedUrl, uploadFileToS3 } from "../services";

/** Maximum annotations per batch request */
const ANNOTATION_BATCH_SIZE = 200;

/** Well-known UUID for embedded/imported PDF annotations - used to identify annotations extracted from the original PDF */
const EMBEDDED_ANNOTATION_USER_ID = "00000000-0000-0000-0000-000000000001";

/** Display name for embedded PDF annotations (must match backend requirement) */
const EMBEDDED_ANNOTATION_USER_NAME = "Embedded";

/**
 * Input parameters for starting a proofing round
 */
export interface StartProofingRoundInput {
  /** ID of the proofing title */
  titleId: string;
  /** Display name of the proofing title (used for S3 path generation) */
  titleName: string;
  /** PDF file to upload */
  file: File;
  /** Due date for the proofing round */
  dueDate: Date;
  /** Optional internal note (not shown to suppliers) */
  internalNote?: string;
  /** List of participant user IDs to add to the round */
  participantUserIds: string[];
  /** IDs of users already registered as title-level participants (used to skip redundant participantAdd calls) */
  existingParticipantIds?: string[];
  /** Extracted annotation XFDF strings from the PDF to import */
  extractedAnnotations?: string[];
}

/**
 * Response data returned after successfully starting a proofing round
 */
export interface StartProofingRoundResult {
  /** ID of the created proofing round */
  roundId: string;
  /** ID of the proofing title */
  titleId: string;
}

/**
 * Custom error interface for file upload failures that require rollback
 */
export interface FileUploadError extends Error {
  name: "FileUploadError";
  originalError?: Error;
}

/**
 * Creates a FileUploadError
 */
export function createFileUploadError(message: string, originalError?: Error): FileUploadError {
  const error = new Error(message) as FileUploadError;
  error.name = "FileUploadError";
  error.originalError = originalError;
  return error;
}

/**
 * Type guard to check if an error is a FileUploadError
 */
export function isFileUploadError(error: unknown): error is FileUploadError {
  return error instanceof Error && error.name === "FileUploadError";
}

export interface UseStartProofingRoundOptions {
  onSuccess?: (data: StartProofingRoundResult) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for orchestrating the complete flow of starting a proofing round.
 *
 * This hook handles the following steps:
 * 1. Creates a new proofing round with the specified due date
 * 2. Gets a signed URL for uploading the document
 * 3. Uploads the PDF file to S3 (if fails, deletes the round and throws)
 * 4. Updates the title state to 'active'
 * 5. Creates an internal note if provided
 * 6. Adds all specified participants to the round
 * 7. Creates the document record linked to the round
 * 8. Imports extracted PDF annotations in batches (if any)
 *
 * If the file upload fails, the proofing round is deleted (rolled back) and
 * a FileUploadError is thrown.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useStartProofingRound = (options?: UseStartProofingRoundOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: StartProofingRoundInput) => {
      const {
        titleId,
        titleName,
        file,
        dueDate,
        internalNote,
        participantUserIds,
        existingParticipantIds = [],
        extractedAnnotations,
      } = input;

      // Step 1: Create proofing round to get roundId
      const roundResponse = await proofingRoundCreate({
        path: { titleId },
        body: {
          dueDate: format(dueDate, "yyyy-MM-dd"),
          state: "reviewing",
        },
      });

      if (!roundResponse.data) {
        throw new Error("Failed to create proofing round");
      }
      const roundId = roundResponse.data.id;

      // Step 2: Get signed upload URL (only needs roundId and title info)
      const { url: signedUrl, documentPath } = await getDocumentUploadSignedUrl({
        proofingTitleId: titleId,
        proofingTitleName: titleName,
        roundId,
        filename: file.name,
      });

      // Step 3: Upload file to S3 - if this fails, rollback by deleting the round
      try {
        await uploadFileToS3(signedUrl, file);
      } catch (uploadError) {
        // Rollback: delete the created proofing round
        let roundDeleted = true;
        try {
          await proofingRoundDelete({
            path: { titleId, roundId },
          });
        } catch (deleteError) {
          // Log delete failure but still throw the upload error
          console.error("Failed to rollback proofing round after upload failure:", deleteError);
          roundDeleted = false;
        }

        // Throw a specific error for file upload failure
        throw createFileUploadError(
          `Failed to upload file. ${roundDeleted ? "The proofing round was not created." : ""}`,
          uploadError instanceof Error ? uploadError : new Error(String(uploadError)),
        );
      }

      // Step 4: Update title state to active
      await proofingTitleUpdate({
        path: { titleId },
        body: { state: "active" },
      });

      // Step 5: Create internal note if provided
      if (internalNote) {
        await internalNoteCreate({
          path: { titleId },
          body: { proofingRoundId: roundId, note: internalNote },
        });
      }

      // Step 6: Add all participants to the title (if not already) and to the round.
      // The backend uses title-level participants to build currentRound.participants,
      // so new participants must be added at both levels.
      // existingParticipantIds skips known title participants to avoid redundant calls.
      const existingParticipantIdSet = new Set(existingParticipantIds);
      for (const userId of participantUserIds) {
        if (!existingParticipantIdSet.has(userId)) {
          await participantAdd({
            path: { titleId },
            body: { userId },
          });
        }
        await participantRoundStatusCreate({
          path: { roundId, userId },
          body: {},
        });
      }

      // Step 7: Create document record
      const documentResponse = await documentCreate({
        path: { roundId },
        body: {
          documentPath,
          originalFilename: file.name,
        },
      });

      if (!documentResponse.data) {
        throw new Error("Failed to create document record");
      }
      const documentId = documentResponse.data.id;

      // Step 8: Import extracted annotations in batches
      if (extractedAnnotations && extractedAnnotations.length > 0) {
        // Chunk annotations into batches
        for (let i = 0; i < extractedAnnotations.length; i += ANNOTATION_BATCH_SIZE) {
          const batch = extractedAnnotations.slice(i, i + ANNOTATION_BATCH_SIZE);

          await annotationCreateBatch({
            path: { documentId },
            body: {
              userId: EMBEDDED_ANNOTATION_USER_ID,
              userName: EMBEDDED_ANNOTATION_USER_NAME,
              annotations: batch,
            },
          });
        }
      }

      return { roundId, titleId };
    },
    onSuccess: (data) => {
      // Invalidate the specific title details to refetch updated data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.detail(data.titleId) });
      // Invalidate all title queries so lists show updated status
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to start proofing round:", error);
      options?.onError?.(error);
    },
  });
};
